import React, { Fragment, useState, forwardRef } from 'react';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import IconToast from '../NewToast';
import { handleError } from '../../utils/openai';
import { openaiGenerateWithTextStreaming } from '../../store/openai/api';
import RecordDialog from '../RecordDialog/RecordDialog';
import LoadingBackdrop from '../LoadingBackdrop/LoadingBackdrop';
import { speechToText } from '../../store/speech/api';
import { LANGUAGE } from '../../constants/language';
import { injectIntl } from 'react-intl';
import NativeOrWeb from '../../utils/native';
import { tracking } from '../../utils/tracking';
import { trackDirect } from '../../store/analytics/api';

// v9: forwardRef render must accept (props, ref). The ref is unused here but
// must be present to silence the React warning.
// eslint-disable-next-line no-unused-vars
const VoiceCreateSosPaperButton = forwardRef((props, ref) => {
  const { intl, addPoliciesByVoice } = props;

  const [initializing, setInitializing] = useState(false);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);

  const openDialog = () => {
    setRecordingDialogOpen(true);
  };

  const startRecording = async () => {
    try {
      setRecording(true);
      setInitializing(true);
      trackDirect('useAiFeature');
      trackDirect('createEmergencyReferenceByVoice');
      tracking('AI: Create Emergency Reference By Voice');

      await NativeOrWeb.startRecordingVoice();
    } catch (error) {
      console.log(error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'error-msg' })), { className: 'new-toast' });
      setRecordingDialogOpen(false);
    } finally {
      setInitializing(false);
    }
  };

  const cancelRecording = () => {
    setRecordingDialogOpen(false);
    setRecording(false);
    if (recording) {
      NativeOrWeb.stopRecordingVoice()
        .then()
        .catch(console.log);
    }
  };

  const stopRecording = async () => {
    try {
      setRecordingDialogOpen(false);
      setLoading(true);

      const recordingData = await NativeOrWeb.stopRecordingVoice();
      const { mimeType, msDuration, recordDataBase64 } = recordingData?.value || {};
      const speechToTextResult = await speechToText({ mimeType, msDuration, recordDataBase64 });

      const { text: speechText, language: detectedLanguage } = speechToTextResult?.result || {};

      let speechLanguage;
      switch (detectedLanguage) {
        case 'en-US':
          speechLanguage = LANGUAGE.EN;
          break;
        case 'zh-HK':
          speechLanguage = LANGUAGE.ZH_HANT_HK;
          break;
        case 'zh-CN':
          speechLanguage = LANGUAGE.ZH;
          break;
        default:
          speechLanguage = LANGUAGE.EN;
          break;
      }

      await openaiGenerateWithTextStreaming(
        'create_sos_paper',
        { speechText: speechText },
        [],
        null,
        addPoliciesByVoice,
        setLoading
      );
    } catch (error) {
      handleError(error);
      setLoading(false);
    } finally {
      setRecording(false);
    }
  };

  return (
    <Fragment>
      <RecordDialog
        title={intl.formatMessage({ id: 'ai-voice-create-sos-paper' })}
        content={intl.formatMessage({ id: 'ai-voice-create-sos-paper-content' })}
        timeLimit={120}
        open={recordingDialogOpen}
        initializing={initializing}
        recording={recording}
        onClose={cancelRecording}
        onStartRecording={startRecording}
        onDoneRecording={stopRecording}
      />
      <LoadingBackdrop open={loading} />
      <Button
        color="primary"
        variant="outlined"
        style={{ padding: '0px 4px', fontSize: 14, marginLeft: 4, minWidth: 'unset' }}
        className="voice"
        onClick={openDialog}
      >
        <img
          style={{
            width: '18px',
            height: '18px',
            paddingRight: '4px'
          }}
          src="/img/icons/ai_bg_free.svg"
        />
        {intl.formatMessage({ id: 'voice' })}
      </Button>
    </Fragment>
  );
});

export default injectIntl(VoiceCreateSosPaperButton);
